import { useState, useCallback, useEffect } from "react";
import { Modal, ModalBody, ModalHeader, Row, Col, FormFeedback, Button, Form } from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useEditVehicleStock } from "hooks/stockHook";
import ButtonTooltip from "components/Common/ButtonTooltip";
import { useToastMessage } from "hooks/useToastMessage";

// Custom Hooks
import WarningAlert from "components/Common/WarningAlert";
import { useVehicleForm } from "hooks/useVehicleForm";
import FormikCreatableField from "components/Common/FormikCreatableField";

export default function StockTechnicalDetailsUpdate({ stock = null }) {
  const [modal, setModal] = useState(false)
  const toggle = useCallback(() => {
    setModal(prev => !prev)
  }, [])

  const { mutateAsync: editStock, isPending: stockEditLoading } =
    useEditVehicleStock()

  const { handleResponse } = useToastMessage()

  // Get the current model ID from stock for filtering
  const vs_model_id = stock?.vs_model_id

  const validationSchema = Yup.object({
    vs_chassis_code_id: Yup.string().nullable(),
    vs_engine_code_id: Yup.string().nullable(),
    vs_auction_grade_id: Yup.string().nullable(),
    vs_engine_cc_id: Yup.string().nullable(),
    vs_fuel_type_id: Yup.string().nullable(),
    vs_engine_type_id: Yup.string().nullable(),
    vs_key_type_id: Yup.string().nullable(),
    vs_transmission_id: Yup.string().nullable(),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      try {
        const res = await editStock({
          id: stock?.id,
          vs_model_id,
          isResetForModel: true,
          ...values,
        })
        handleResponse(res, "Technical details updated successfully.", true)
        if (res.success) {
          toggle()
          resetForm()
        }
      } catch (error) {
        console.error("Failed to update technical details:", error)
      }
    },
    [editStock, stock, handleResponse]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      vs_chassis_code_id: stock?.vs_chassis_code_id || "",
      vs_engine_code_id: stock?.vs_engine_code_id || "",
      vs_auction_grade_id: stock?.vs_auction_grade_id || "",
      vs_engine_cc_id: stock?.vs_engine_cc_id || "",
      vs_fuel_type_id: stock?.vs_fuel_type_id || "",
      vs_engine_type_id: stock?.vs_engine_type_id || "",
      vs_key_type_id: stock?.vs_key_type_id || "",
      vs_transmission_id: stock?.vs_transmission_id || "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const {
    handleParams,
    optionLoading,
    engineCodesData,
    chassisCodesData,
    engineCcsData,
    auctionGradesData,
    transmissionsData,
    engineTypesData,
    fuelTypesData,
    keyTypesData,
  } = useVehicleForm({ stockResponse: stock, isEdit: true, formik })

  // ✅ Set initial model id only if it exists
  const modelSlug = stock?.model?.slug || ""

  useEffect(() => {
    if (modelSlug) {
      handleParams("model", modelSlug)
    }
  }, [modelSlug, handleParams])

  return (
    <>
      <ButtonTooltip
        type="edit"
        onClick={toggle}
        btnId="edit-technical-details"
        btnName="Edit Technical Details"
      />

      <Modal isOpen={modal} toggle={toggle} centered size="lg">
        <ModalHeader toggle={toggle}>Edit Technical Details</ModalHeader>
        <ModalBody>
          {!vs_model_id && (
            <WarningAlert message="If you want to change the 'Engine CC', 'Chassis Code', or 'Engine Code', please update the 'Vehicle Model' in the 'Specifications' section first." />
          )}
          <Form
            onSubmit={e => {
              e.preventDefault()
              formik.handleSubmit()
            }}
          >
            <Row>
              {/* Engine CC */}
              <Col md={6}>
                <FormikCreatableField
                  name="vs_engine_cc_id"
                  label="Engine CC"
                  formik={formik}
                  data={engineCcsData}
                  onChange={formik.handleChange}
                  placeholder="Select CC"
                  isLoading={optionLoading}
                />
                {formik.touched.vs_engine_cc_id &&
                  formik.errors.vs_engine_cc_id && (
                    <FormFeedback>{formik.errors.vs_engine_cc_id}</FormFeedback>
                  )}
              </Col>

              {/* Chassis Code */}
              <Col md={6}>
                <FormikCreatableField
                  name="vs_chassis_code_id"
                  label="Chassis Code"
                  formik={formik}
                  data={chassisCodesData}
                  onChange={formik.handleChange}
                  placeholder="Select Code"
                  isLoading={optionLoading}
                />
                {formik.touched.vs_chassis_code_id &&
                  formik.errors.vs_chassis_code_id && (
                    <FormFeedback>
                      {formik.errors.vs_chassis_code_id}
                    </FormFeedback>
                  )}
              </Col>

              {/* Engine Code */}
              <Col md={6}>
                <FormikCreatableField
                  name="vs_engine_code_id"
                  label="Engine Code"
                  formik={formik}
                  data={engineCodesData}
                  onChange={formik.handleChange}
                  placeholder="Select Code"
                  isLoading={optionLoading}
                />
                {formik.touched.vs_engine_code_id &&
                  formik.errors.vs_engine_code_id && (
                    <FormFeedback>
                      {formik.errors.vs_engine_code_id}
                    </FormFeedback>
                  )}
              </Col>

              {/* Fuel Type */}
              <Col md={6}>
                <FormikCreatableField
                  name="vs_fuel_type_id"
                  label="Fuel Type"
                  formik={formik}
                  data={fuelTypesData}
                  onChange={formik.handleChange}
                  placeholder="Select Type"
                  isLoading={optionLoading}
                />
                {formik.touched.vs_fuel_type_id &&
                  formik.errors.vs_fuel_type_id && (
                    <FormFeedback>{formik.errors.vs_fuel_type_id}</FormFeedback>
                  )}
              </Col>

              {/* Engine Type */}
              <Col md={6}>
                <FormikCreatableField
                  name="vs_engine_type_id"
                  label="Engine Type"
                  formik={formik}
                  data={engineTypesData}
                  onChange={formik.handleChange}
                  placeholder="Select Type"
                  isLoading={optionLoading}
                />
                {formik.touched.vs_engine_type_id &&
                  formik.errors.vs_engine_type_id && (
                    <FormFeedback>
                      {formik.errors.vs_engine_type_id}
                    </FormFeedback>
                  )}
              </Col>

              {/* Key Type */}
              <Col md={6}>
                <FormikCreatableField
                  name="vs_key_type_id"
                  label="Key Type"
                  formik={formik}
                  data={keyTypesData}
                  onChange={formik.handleChange}
                  placeholder="Select Type"
                  isLoading={optionLoading}
                />
                {formik.touched.vs_key_type_id &&
                  formik.errors.vs_key_type_id && (
                    <FormFeedback>{formik.errors.vs_key_type_id}</FormFeedback>
                  )}
              </Col>

              {/* Auction Grade */}
              <Col md={6}>
                <FormikCreatableField
                  name="vs_auction_grade_id"
                  label="Auction Grade"
                  formik={formik}
                  data={auctionGradesData}
                  onChange={formik.handleChange}
                  placeholder="Select Grade"
                  isLoading={optionLoading}
                />
                {formik.touched.vs_auction_grade_id &&
                  formik.errors.vs_auction_grade_id && (
                    <FormFeedback>
                      {formik.errors.vs_auction_grade_id}
                    </FormFeedback>
                  )}
              </Col>

              {/* Transmission */}
              <Col md={6}>
                <FormikCreatableField
                  name="vs_transmission_id"
                  label="Transmission"
                  formik={formik}
                  data={transmissionsData}
                  onChange={formik.handleChange}
                  placeholder="Select Transmission"
                  isLoading={optionLoading}
                />
                {formik.touched.vs_transmission_id &&
                  formik.errors.vs_transmission_id && (
                    <FormFeedback>
                      {formik.errors.vs_transmission_id}
                    </FormFeedback>
                  )}
              </Col>
            </Row>

            <div className="text-end mt-4">
              <Button type="submit" color="success" disabled={stockEditLoading}>
                Save Changes
                {stockEditLoading && "..."}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </>
  )
}
